import { useState } from "react";
import { Modal } from "@/components/ui/modal";
import { attachmentService } from "@/services/attachmentService";
import type { Attachment } from "@/types/models";

interface AttachmentViewerProps {
  sourceType: string;
  sourceId: number;
  count: number;
}

export default function AttachmentViewer({
  sourceType,
  sourceId,
  count,
}: AttachmentViewerProps) {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);

  if (count <= 0) return null;

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const data = await attachmentService.listBySource(sourceType, sourceId);
      setAttachments(data ?? []);
    } catch {
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const isImage = (ct: string) =>
    ct === "image/jpeg" || ct === "image/png";

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-1 text-gray-400 hover:text-brand-500 dark:hover:text-brand-400"
        title="View receipts"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"
          />
        </svg>
        <span className="text-xs">{count}</span>
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        className="max-w-lg p-5 sm:p-6 mx-4"
      >
        <h4 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
          Receipts / Invoices
        </h4>

        {loading ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading...
          </p>
        ) : attachments.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No receipts attached.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {attachments.map((a) => (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
              >
                {isImage(a.content_type) ? (
                  <img
                    src={a.url}
                    alt={a.file_name}
                    className="h-32 w-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-32 flex-col items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <span className="text-2xl font-bold text-error-500">PDF</span>
                  </div>
                )}
                <div className="px-2 py-1.5">
                  <p className="truncate text-xs text-gray-600 dark:text-gray-400">
                    {a.file_name}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {(a.size_bytes / 1024).toFixed(0)} KB
                  </p>
                </div>
              </a>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
